<template>
  <div class="mobile-layout">
    <!-- Top Bar -->
    <div class="top-bar">
      <div class="top-bar-left">
        <h1 class="app-title">{{ pageTitle }}</h1>
      </div>
      <div class="top-bar-right">
        <button class="icon-btn" @click="goToProfile" title="个人中心">
          👤
        </button>
        <button class="icon-btn" @click="$switchLang($i18n.locale === 'zh-CN' ? 'en-US' : 'zh-CN')" title="切换语言">
          {{ $i18n.locale === 'zh-CN' ? 'EN' : '中' }}
        </button>
        <button class="icon-btn" @click="handleLogout" title="退出登录">
          🚪
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </div>

    <!-- Bottom Navigation -->
    <div class="bottom-nav" v-if="showBottomNav">
      <button 
        v-for="item in navItems" 
        :key="item.path"
        class="nav-item"
        :class="{ active: isActive(item.path) }"
        @click="$router.push(item.path)"
      >
        <span class="nav-icon">{{ item.icon }}</span>
        <span class="nav-text">{{ item.label }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { getMenuByIdentity } from '@/config/menu.config';

export default {
  name: 'MobileLayout',
  data() {
    return {
      navItems: []
    };
  },
  computed: {
    pageTitle() {
      const route = this.$route;
      const matched = route.matched[route.matched.length - 1];
      return matched?.meta?.title || 'NAPORLUN';
    },
    showBottomNav() {
      const path = this.$route.path;
      // Show bottom nav on main pages, not on detail/edit pages
      return !path.includes('/product/') && 
             !path.includes('/create') && 
             !path.includes('/edit') &&
             (path.startsWith('/wholesaler/') || path.startsWith('/stall/') || path.startsWith('/delivery/'));
    }
  },
  methods: {
    isActive(path) {
      return this.$route.path === path || this.$route.path.startsWith(path + '/');
    },
    goToProfile() {
      // 根据角色跳转到对应的个人中心
      if (localStorage.getItem('driver_id')) {
        this.$router.push('/delivery/profile');
      } else {
        this.$router.push('/my-profile');
      }
    },
    handleLogout() {
      if (confirm('确定要退出登录吗？')) {
        // 调用 auth.js 的 logout 方法统一处理
        this.$store.dispatch('auth/logout');
        // 强制刷新页面
        window.location.href = '/login';
      }
    },
    loadNavItems() {
      // 根据身份加载底部导航
      const identity = localStorage.getItem('user_identity');
      const menuItems = getMenuByIdentity(identity);
      
      // 只取前 5 个作为底部导航
      this.navItems = menuItems.slice(0, 5).map(item => ({
        path: item.path,
        label: item.title,
        icon: item.icon
      }));
    }
  },
  created() {
    this.loadNavItems();
  }
};
</script>

<style scoped>
.mobile-layout {
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

/* ========== Top Bar ========== */
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top, 0px));
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  pointer-events: auto;
}

.top-bar-right {
  display: flex;
  gap: 8px;
  pointer-events: auto;
}

.top-bar-left {
  flex: 1;
}

.app-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.top-bar-right {
  display: flex;
  gap: 8px;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: background 0.2s;
  position: relative;
  z-index: 101;
  pointer-events: auto;
}

.icon-btn:active {
  background: #f5f5f5;
}

.icon-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

/* ========== Main Content ========== */
.main-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom, 60px);
}

/* ========== Fade Transition ========== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ========== Bottom Navigation ========== */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  z-index: 500;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-icon {
  font-size: 20px;
  line-height: 1;
}

.nav-text {
  font-size: 10px;
  color: #999;
  font-weight: 500;
}

.nav-item.active .nav-text {
  color: var(--primary, #FF6B35);
}

.nav-item:active {
  transform: scale(0.95);
}

@media (max-width: 768px) {
  .bottom-nav {
    font-size: 12px;
  }
  
  .nav-icon {
    font-size: 18px;
  }
}
</style>
